import { ICampaignTriggerMessage } from '@dataTypes/marketing.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_MARKETING', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type CampaignsTriggerData = {
  campaign_trigger_messages?: ICampaignTriggerMessage[]
}

type GenericResponseData = Record<string, unknown>

type UpdateCampaignTriggerResponse = ApiResponse<CampaignsTriggerData>
type CreateCampaignTriggerResponse = ApiResponse<GenericResponseData>
type CreateMultipleCampaignTriggerResponse = ApiResponse<CampaignsTriggerData>

// API Functions
export const updateTriggerMessageById = async (
  campaign_trigger_message_id: string,
  updatedData: Partial<ICampaignTriggerMessage>,
): Promise<UpdateCampaignTriggerResponse> => {
  try {
    const response = await axios.put<UpdateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-message`,
      { campaign_trigger_message_id, data: updatedData },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateTriggerMessageById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateMultipleTriggerMessages = async (
  messages: ICampaignTriggerMessage[],
): Promise<UpdateCampaignTriggerResponse> => {
  try {
    const response = await axios.put<UpdateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-message/bulk`,
      { campaign_trigger_messages: messages },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateMultipleTriggerMessages:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createTriggerMessage = async (
  triggerDetails: ICampaignTriggerMessage,
): Promise<CreateCampaignTriggerResponse> => {
  try {
    const response = await axios.post<CreateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-message`,
      triggerDetails,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createTriggerMessage:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createMultipleTriggerMessages = async (
  messages: ICampaignTriggerMessage[],
): Promise<CreateMultipleCampaignTriggerResponse> => {
  try {
    const response = await axios.post<CreateMultipleCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-message/bulk`,
      { campaign_trigger_messages: messages },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createMultipleTriggerMessages:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteTriggerMessageById = async (
  campaignTriggerMessageId: string,
): Promise<CreateCampaignTriggerResponse> => {
  try {
    const response = await axios.delete<CreateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-message/${campaignTriggerMessageId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteTriggerMessageById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteMultipleTriggerMessages = async (
  campaignTriggerMessageIds: string[],
): Promise<CreateCampaignTriggerResponse> => {
  try {
    const response = await axios.delete<CreateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-message/bulk`,
      {
        headers,
        data: {
          campaign_trigger_message_ids: campaignTriggerMessageIds,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteMultipleTriggerMessages:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
